/* NOOR PERFUMES — Shopify theme runtime.

   Port of the static site's js/app.js. Every interaction from the original is
   preserved (bottom-sheet drawer with drag-to-close and swipe-to-open, the
   slide-up product info panel, tap-to-zoom on touch, live search, "show more",
   the header brand morph, the Escape stack).

   What changed: the localStorage {id: qty} cart is gone. All cart state now
   lives in Shopify and is mutated through /cart/add.js, /cart/change.js and
   /cart/update.js. Nothing is priced or formatted in JavaScript — after every
   mutation the affected section is re-rendered server-side through the Section
   Rendering API, so the money filter stays the single source of truth for
   currency formatting. The Supabase catalogue loader and the Mollie checkout
   form are deleted; checkout is Shopify's. */

(function () {
  "use strict";

  var CFG = window.NOOR_THEME || {};
  var ROUTES = CFG.routes || {};
  var STR = CFG.strings || {};

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }

  /* ================================================================
     Section Rendering API helpers
     ================================================================ */

  function fetchSection(sectionId, url) {
    var u;
    try {
      u = new URL(url || (window.location.pathname + window.location.search), window.location.origin);
    } catch (e) {
      u = new URL(window.location.href);
    }
    u.searchParams.set("sections", sectionId);
    return fetch(u.toString(), { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("Section request failed: " + r.status);
        return r.json();
      })
      .then(function (json) { return json[sectionId]; });
  }

  function replaceSection(sectionId, html) {
    if (!html) return null;
    var current = document.getElementById("shopify-section-" + sectionId);
    if (!current) return null;
    var holder = document.createElement("div");
    holder.innerHTML = html;
    var fresh = holder.firstElementChild;
    if (!fresh) return null;
    current.parentNode.replaceChild(fresh, current);
    return fresh;
  }

  /* ================================================================
     Cart
     ================================================================ */

  var cartBar = qs("#cartBar");
  var cartSpacer = qs("#cartSpacer");
  var cartCount = qs("#cartCount");
  var cartDrawer = qs("#cartDrawer");
  var cartBackdrop = qs("#cartBackdrop");
  var drawerHandle = qs("#drawerHandle");

  function syncCartChrome(count) {
    if (typeof count !== "number" || isNaN(count)) return;
    if (cartCount) cartCount.textContent = count;
    if (cartBar) cartBar.classList.toggle("show", count > 0);
    if (cartSpacer) cartSpacer.classList.toggle("show", count > 0);
    var headerCart = qs("#headerCartLink");
    if (headerCart) {
      headerCart.classList.toggle("is-visible", count > 0);
      var n = qs("[data-header-cart-count]", headerCart);
      if (n) n.textContent = count;
    }
    document.documentElement.classList.toggle("cart-has-items", count > 0);
  }

  /* Re-render the drawer from the server, then read the authoritative item
     count straight off the fresh markup (saves a second round trip to
     /cart.js just to learn the count). Shopify owns the outer
     #shopify-section-* wrapper, so the count is carried on an inner element. */
  function readCartCount(scope) {
    var el = (scope || document).querySelector("[data-cart-count]");
    if (!el) return NaN;
    return parseInt(el.getAttribute("data-cart-count"), 10);
  }

  function refreshDrawer() {
    /* The cart-drawer section's content depends only on `cart`, never on
       whichever page asked for it. Rendering it against the shop's own root
       URL (instead of the current page) sidesteps whatever is going wrong
       when the Section Rendering API is asked to re-render a full product
       page just to extract this one section from it. */
    return fetchSection("cart-drawer", ROUTES.root_url || "/").then(function (html) {
      return replaceSection("cart-drawer", html);
    });
  }

  /* Authoritative, always-reliable cart count. Independent of the section
     re-render above, which can fail to resolve depending on which page
     triggered it (e.g. the product page) and would otherwise leave the
     header/bottom-bar count stale until the next full page load. */
  function refreshCartCount() {
    return fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        syncCartChrome(cart.item_count);
        return cart;
      });
  }

  function refreshCartPage() {
    var onCartPage = !!document.getElementById("shopify-section-main-cart");
    var jobs = [refreshCartCount(), refreshDrawer()];
    if (onCartPage) {
      jobs.push(fetchSection("main-cart").then(function (html) {
        replaceSection("main-cart", html);
      }));
    }
    return Promise.all(jobs);
  }

  function cartError(message) {
    var box = qs("[data-cart-error]");
    if (box) box.textContent = message || "";
  }

  function addToCart(variantId, quantity, opts) {
    opts = opts || {};
    cartError("");
    var body = { items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }] };
    return fetch(ROUTES.cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || STR.cart_error);
          return data;
        });
      })
      .then(function () {
        return refreshCartPage().then(function () {
          if (opts.openDrawer) openDrawer();
        });
      })
      .catch(function (err) {
        cartError(err && err.message ? err.message : STR.cart_error);
        throw err;
      });
  }

  function changeLine(lineKey, quantity) {
    cartError("");
    return fetch(ROUTES.cart_change_url || "/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, quantity: Number(quantity) })
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || STR.cart_error);
          return data;
        });
      })
      .then(function (cart) {
        return refreshCartPage().then(function () { return cart; });
      })
      .catch(function (err) {
        cartError(err && err.message ? err.message : STR.cart_error);
      });
  }

  /* Delegated: works across drawer and cart-page re-renders alike. */
  on(document, "click", function (e) {
    var minus = e.target.closest("[data-qty-minus]");
    if (minus) {
      e.preventDefault();
      var kM = minus.getAttribute("data-line-key");
      var qM = parseInt(minus.getAttribute("data-qty-minus"), 10);
      setBusy(minus);
      changeLine(kM, Math.max(0, qM - 1));
      return;
    }
    var plus = e.target.closest("[data-qty-plus]");
    if (plus) {
      e.preventDefault();
      var kP = plus.getAttribute("data-line-key");
      var qP = parseInt(plus.getAttribute("data-qty-plus"), 10);
      setBusy(plus);
      changeLine(kP, Math.min(99, qP + 1));
      return;
    }
    var remove = e.target.closest("[data-line-remove]");
    if (remove) {
      e.preventDefault();
      setBusy(remove);
      changeLine(remove.getAttribute("data-line-remove"), 0);
    }
  });

  function setBusy(el) {
    var row = el.closest(".cart-row");
    if (row) row.classList.add("cart-loading");
  }

  /* Quick add straight from a product card. */
  on(document, "click", function (e) {
    var btn = e.target.closest("button.order-btn[data-variant-id]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    btn.classList.remove("flash");
    void btn.offsetWidth;
    btn.classList.add("flash");

    btn.disabled = true;
    addToCart(btn.getAttribute("data-variant-id"), 1, { openDrawer: false })
      .catch(function () { /* message already surfaced */ })
      .then(function () { btn.disabled = false; });
  });

  /* Cart note (cart page only). */
  on(document, "change", function (e) {
    var note = e.target.closest("[data-cart-note]");
    if (!note) return;
    fetch(ROUTES.cart_update_url || "/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ note: note.value })
    });
  });

  /* ================================================================
     Cart drawer — bottom sheet (unchanged behaviour from the static site)
     ================================================================ */

  function openDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add("open");
    cartDrawer.setAttribute("aria-hidden", "false");
    if (cartBackdrop) cartBackdrop.classList.add("show");
    document.body.style.overflow = "hidden";
  }
  function closeDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove("open");
    cartDrawer.setAttribute("aria-hidden", "true");
    if (cartBackdrop) cartBackdrop.classList.remove("show");
    document.body.style.overflow = "";
  }

  on(qs("#cartBarBtn"), "click", openDrawer);
  on(cartBackdrop, "click", closeDrawer);
  on(drawerHandle, "click", closeDrawer);
  on(document, "click", function (e) {
    if (e.target.closest("[data-open-cart-drawer]")) {
      e.preventDefault();
      openDrawer();
    }
  });

  /* drag the handle down to close */
  if (drawerHandle && cartDrawer) {
    (function () {
      var startY = null;
      var currentY = 0;
      function onStart(e) {
        startY = e.touches ? e.touches[0].clientY : e.clientY;
        cartDrawer.style.transition = "none";
      }
      function onMove(e) {
        if (startY === null) return;
        var y = e.touches ? e.touches[0].clientY : e.clientY;
        currentY = Math.max(0, y - startY);
        cartDrawer.style.transform = "translateY(" + currentY + "px)";
      }
      function onEnd() {
        if (startY === null) return;
        cartDrawer.style.transition = "";
        cartDrawer.style.transform = "";
        if (currentY > 90) closeDrawer();
        startY = null;
        currentY = 0;
      }
      drawerHandle.addEventListener("touchstart", onStart, { passive: true });
      drawerHandle.addEventListener("touchmove", onMove, { passive: true });
      drawerHandle.addEventListener("touchend", onEnd);
      drawerHandle.addEventListener("mousedown", onStart);
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onEnd);
    })();
  }

  /* swipe up on the cart bar (mobile) opens the drawer */
  if (cartBar) {
    (function () {
      var startY = null;
      cartBar.addEventListener("touchstart", function (e) {
        startY = e.touches[0].clientY;
      }, { passive: true });
      cartBar.addEventListener("touchend", function (e) {
        if (startY === null) return;
        var endY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : startY;
        if (startY - endY > 30) openDrawer();
        startY = null;
      });
    })();
  }

  /* Defensive: document.body.style.overflow = "hidden" (set above while the
     drawer is open) is an INLINE style, so it beats any external stylesheet
     rule outright - including position:sticky on the header, which silently
     stops working the moment body can no longer establish a normal scrolling
     context. The one realistic way this survives past closeDrawer() is a
     browser back/forward restore: a customer opens the drawer, clicks
     Checkout (full navigation away, drawer never explicitly closed), then
     hits Back - bfcache can restore the page exactly as it was, inline style
     included. Force it sane on every such restore, and once on first load in
     case anything else ever leaves it stuck. */
  function forceCartChromeSane() {
    if (!cartDrawer || !cartDrawer.classList.contains("open")) {
      document.body.style.overflow = "";
    }
  }
  on(window, "pageshow", forceCartChromeSane);
  forceCartChromeSane();

  /* ================================================================
     Header height sync (drawer top offset)
     ================================================================ */

  var siteHeader = qs(".site-header");
  function syncHeaderHeight() {
    if (!siteHeader) return;
    document.documentElement.style.setProperty("--header-h", siteHeader.offsetHeight + "px");
  }
  syncHeaderHeight();
  on(window, "resize", syncHeaderHeight);

  /* ================================================================
     Product grid — info panel, touch zoom, search, show more
     ================================================================ */

  function setInfo(block, open) {
    if (!block) return;
    var meta = qs(".product-meta", block);
    var panel = qs(".info-swipe", block);
    block.classList.toggle("info-open", open);
    if (meta) meta.setAttribute("aria-expanded", open ? "true" : "false");
    if (panel) panel.setAttribute("aria-hidden", open ? "false" : "true");
  }
  function toggleInfo(block) {
    if (!block) return;
    var willOpen = !block.classList.contains("info-open");
    qsa(".product-block.info-open").forEach(function (b) { setInfo(b, false); });
    if (willOpen) setInfo(block, true);
  }

  /* One delegated listener on the document rather than per-grid, so grids
     added by "show more" or re-rendered sections keep working. */
  on(document, "click", function (e) {
    if (!e.target.closest(".product-grid")) return;

    var close = e.target.closest(".swipe-close");
    if (close) {
      e.preventDefault();
      setInfo(close.closest(".product-block"), false);
      return;
    }
    /* The empty area below the notes/fact/price is itself a tap-to-close
       target, same as the × button. */
    var dismiss = e.target.closest(".swipe-dismiss");
    if (dismiss) {
      setInfo(dismiss.closest(".product-block"), false);
      return;
    }
    /* Links inside the panel (product page) must not toggle it shut, and the
       title link should navigate to the product page rather than open it. */
    if (e.target.closest(".info-swipe a")) return;
    if (e.target.closest(".meta-name a")) return;

    var meta = e.target.closest(".product-meta");
    if (meta && !e.target.closest(".order-btn") && !e.target.closest(".waitlist-panel")) {
      toggleInfo(meta.closest(".product-block"));
    }
  });

  on(document, "keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " ") return;
    if (e.target.closest(".waitlist-panel")) return;
    if (e.target.closest(".meta-name a")) return;
    var meta = e.target.closest(".product-meta");
    if (!meta) return;
    e.preventDefault();
    toggleInfo(meta.closest(".product-block"));
  });

  /* ================================================================
     Waitlist (sold-out products): the button becomes OK and doubles as the
     submit control; the email field grows out of it to its left at exactly
     the button's height, so the two read as one continuous rectangle.
     Submits through Shopify's own customer form (form_type=customer) - no
     app, no backend. The merchant filters Customers by the "waitlist" tag.
     ================================================================ */

  function closeWaitlist(meta) {
    if (!meta) return;
    meta.classList.remove("waitlist-open");
    var b = qs("[data-waitlist]", meta);
    var p = qs(".waitlist-panel", meta);
    if (b) {
      b.setAttribute("aria-expanded", "false");
      var label = b.getAttribute("data-label");
      if (label) b.textContent = label;
    }
    if (p) p.setAttribute("aria-hidden", "true");
  }

  function toggleWaitlist(btn) {
    var meta = btn.closest(".product-meta");
    if (!meta) return;
    qsa(".product-meta.waitlist-open").forEach(function (m) {
      if (m !== meta) closeWaitlist(m);
    });
    if (meta.classList.contains("waitlist-open")) {
      closeWaitlist(meta);
      return;
    }
    meta.classList.add("waitlist-open");
    btn.setAttribute("aria-expanded", "true");
    if (!btn.getAttribute("data-label")) btn.setAttribute("data-label", btn.textContent);
    btn.textContent = STR.ok || "OK";
    var panel = qs(".waitlist-panel", meta);
    if (panel) {
      panel.setAttribute("aria-hidden", "false");
      var input = qs(".waitlist-input", panel);
      if (input) setTimeout(function () { input.focus(); }, 80);
    }
  }

  function submitWaitlist(btn) {
    var meta = btn.closest(".product-meta");
    var panel = meta && qs(".waitlist-panel", meta);
    var form = panel && qs("form", panel);
    var input = panel && qs(".waitlist-input", panel);
    if (!form || !input) return;

    var email = input.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      input.classList.add("invalid");
      input.focus();
      return;
    }
    input.classList.remove("invalid");
    btn.disabled = true;

    fetch(form.action, { method: "POST", credentials: "same-origin", body: new FormData(form) })
      .then(function (r) {
        if (!r.ok) throw new Error("Waitlist signup failed: " + r.status);
        panel.innerHTML = '<div class="waitlist-done">' + (STR.waitlist_done || "You are on the list.") + "</div>";
        setTimeout(function () { closeWaitlist(meta); }, 1800);
      })
      .catch(function () {
        btn.disabled = false;
        input.classList.add("invalid");
      });
  }

  on(document, "click", function (e) {
    var waitBtn = e.target.closest("[data-waitlist]");
    if (!waitBtn) return;
    e.preventDefault();
    e.stopPropagation();
    var meta = waitBtn.closest(".product-meta");
    if (meta && meta.classList.contains("waitlist-open")) {
      submitWaitlist(waitBtn);
    } else {
      toggleWaitlist(waitBtn);
    }
  });

  on(document, "keydown", function (e) {
    if (e.key !== "Enter") return;
    var input = e.target.closest(".waitlist-input");
    if (!input) return;
    e.preventDefault();
    e.stopPropagation();
    var meta = input.closest(".product-meta");
    var btn = meta && qs("[data-waitlist]", meta);
    if (btn) submitWaitlist(btn);
  });

  /* Touch devices have no hover, so a tap toggles the zoom class explicitly. */
  var isTouchDevice = window.matchMedia &&
    (window.matchMedia("(hover: none)").matches || window.matchMedia("(pointer: coarse)").matches);
  if (isTouchDevice) {
    on(document, "click", function (e) {
      var photo = e.target.closest(".product-grid .product-photo");
      if (!photo) return;
      if (e.target.closest("a")) return;
      var wasZoomed = photo.classList.contains("is-zoomed");
      qsa(".product-photo.is-zoomed").forEach(function (p) {
        if (p !== photo) p.classList.remove("is-zoomed");
      });
      photo.classList.toggle("is-zoomed", !wasZoomed);
    });
  }

  /* ---------- live search over the products currently rendered ----------
     The static site filtered a 200-item array held in memory. Shopify renders
     products a page at a time, so this filters what is on screen and offers a
     link into Shopify's real server-side search for everything else. */
  function initSearchFilter(scope) {
    var input = qs("[data-grid-search]", scope);
    if (!input) return;
    var grid = qs(".product-grid", scope);
    var countEl = qs("[data-grid-count]", scope);
    var loadMoreWrap = qs("[data-load-more-wrap]", scope);
    if (!grid) return;

    var debounce;
    var noResults = null;

    function apply() {
      var q = input.value.trim().toLowerCase();
      var blocks = qsa(".product-block", grid);
      var shown = 0;

      blocks.forEach(function (b) {
        var hay = (b.getAttribute("data-search") || "").toLowerCase();
        var match = !q || hay.indexOf(q) >= 0;
        b.style.display = match ? "" : "none";
        if (match) shown++;
        if (!match) setInfo(b, false);
      });

      if (countEl) {
        countEl.textContent = shown + " " + (shown === 1 ? STR.perfume : STR.perfumes);
      }
      if (loadMoreWrap) loadMoreWrap.style.display = q ? "none" : "";

      if (shown === 0 && q) {
        if (!noResults) {
          noResults = document.createElement("div");
          noResults.className = "no-results";
          var searchUrl = (ROUTES.search_url || "/search") + "?q=" + encodeURIComponent(q);
          noResults.innerHTML =
            '<div class="no-results-title"></div>' +
            '<button type="button" class="no-results-clear"></button>' +
            '<a class="no-results-alt" href="' + searchUrl + '"></a>';
          qs(".no-results-title", noResults).textContent = STR.no_results;
          qs(".no-results-clear", noResults).textContent = STR.clear_search;
          qs(".no-results-alt", noResults).textContent = STR.search_all;
          qs(".no-results-clear", noResults).addEventListener("click", function () {
            input.value = "";
            apply();
            input.focus();
          });
          grid.parentNode.insertBefore(noResults, grid.nextSibling);
        } else {
          var alt = qs(".no-results-alt", noResults);
          if (alt) alt.href = (ROUTES.search_url || "/search") + "?q=" + encodeURIComponent(q);
        }
        noResults.style.display = "";
        grid.classList.add("grid-empty");
        grid.style.display = "none";
      } else {
        if (noResults) noResults.style.display = "none";
        grid.classList.remove("grid-empty");
        grid.style.display = "";
      }

      if (window.NOOR_HEADER_SYNC) window.NOOR_HEADER_SYNC();
    }

    on(input, "input", function () {
      clearTimeout(debounce);
      debounce = setTimeout(apply, 120);
    });
    /* Enter hands off to Shopify's real search. */
    on(input, "keydown", function (e) {
      if (e.key !== "Enter") return;
      e.preventDefault();
      var q = input.value.trim();
      if (q) window.location.href = (ROUTES.search_url || "/search") + "?q=" + encodeURIComponent(q);
    });
  }

  /* ---------- show more: append the next paginated page of this section ---------- */
  on(document, "click", function (e) {
    var btn = e.target.closest("[data-load-more]");
    if (!btn) return;
    e.preventDefault();

    var nextUrl = btn.getAttribute("data-next-url");
    var sectionId = btn.getAttribute("data-section-id");
    if (!nextUrl || btn.disabled) return;

    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = STR.loading || "Loading…";

    var url = nextUrl + (nextUrl.indexOf("?") >= 0 ? "&" : "?") + "section_id=" + encodeURIComponent(sectionId);

    fetch(url, { credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("Load more failed: " + r.status);
        return r.text();
      })
      .then(function (html) {
        var holder = document.createElement("div");
        holder.innerHTML = html;

        var grid = qs(".product-grid", btn.closest("[data-section-root]") || document);
        var incoming = qsa(".product-block", holder);
        incoming.forEach(function (b) { grid.appendChild(b); });

        var newBtn = qs("[data-load-more]", holder);
        if (newBtn && newBtn.getAttribute("data-next-url")) {
          btn.setAttribute("data-next-url", newBtn.getAttribute("data-next-url"));
          btn.disabled = false;
          btn.textContent = original;
        } else {
          var wrap = btn.closest("[data-load-more-wrap]");
          if (wrap) wrap.style.display = "none";
        }
        if (window.NOOR_HEADER_SYNC) window.NOOR_HEADER_SYNC();
      })
      .catch(function () {
        /* Fall back to a plain navigation so the customer is never stuck. */
        window.location.href = nextUrl;
      });
  });

  qsa("[data-section-root]").forEach(initSearchFilter);

  /* ================================================================
     Header brand morph
     ================================================================ */

  (function headerMorph() {
    var header = qs("#siteHeader");
    var brandNoor = qs("#brandNoor");
    var brandAlt = qs("#brandAlt");
    var headerLogo = qs("#headerLogo");
    if (!header || !brandNoor || !brandAlt) return;

    var FADE = 280;
    var ticking = false;

    /* The static site anchored the morph to the last product that had a real
       photo, because only 7 of its 200 items did. Every real Shopify product
       has an image, so the anchor is now the Nth card, configured on the
       header section (default 8), falling back to the last card. */
    function anchorEl() {
      var grid = qs(".product-grid");
      if (!grid) return null;
      var blocks = qsa(".product-block", grid).filter(function (b) {
        return b.style.display !== "none";
      });
      if (!blocks.length) return null;
      var n = parseInt(header.getAttribute("data-morph-after"), 10);
      if (!n || isNaN(n) || n < 1) n = blocks.length;
      return blocks[Math.min(n, blocks.length) - 1];
    }

    function apply() {
      ticking = false;
      var hb = header.getBoundingClientRect().bottom;
      var ref = anchorEl();
      if (!ref) {
        brandNoor.style.opacity = "1";
        brandAlt.style.opacity = "0";
        if (headerLogo) {
          headerLogo.style.opacity = "0";
          headerLogo.classList.remove("is-visible");
        }
        return;
      }
      var rb = ref.getBoundingClientRect().bottom;
      var m = (hb + FADE - rb) / FADE;
      m = m < 0 ? 0 : m > 1 ? 1 : m;
      brandNoor.style.opacity = String(1 - m);
      brandAlt.style.opacity = String(m);
      if (headerLogo) {
        headerLogo.style.opacity = String(m);
        headerLogo.classList.toggle("is-visible", m >= 0.05);
      }
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(apply);
      }
    }

    on(window, "scroll", onScroll, { passive: true });
    on(window, "resize", onScroll);
    window.NOOR_HEADER_SYNC = apply;
    apply();

    on(headerLogo, "click", function () {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  })();

  /* ================================================================
     Product page — gallery, variants, quantity, add to cart
     ================================================================ */

  function initProductForm(root) {
    var data = qs("[data-variant-json]", root);
    if (!data) return;

    var variants;
    try { variants = JSON.parse(data.textContent); } catch (e) { variants = []; }

    /* Selected structurally rather than by data-attribute: Liquid's {% form %}
       and image_tag tags do not reliably accept hyphenated attribute args. */
    var form = qs('form[action*="/cart/add"]', root);
    var idInput = qs("[data-variant-id-input]", root);
    var priceWrap = qs("[data-price-wrap]", root);
    var availability = qs("[data-availability]", root);
    var addBtn = qs("[data-add-to-cart]", root);
    var mainImg = qs(".product-media-main img", root);

    function selectedOptions() {
      return qsa("[data-option-index]", root)
        .filter(function (el) {
          return el.tagName === "SELECT" || el.checked;
        })
        .sort(function (a, b) {
          return Number(a.getAttribute("data-option-index")) - Number(b.getAttribute("data-option-index"));
        })
        .map(function (el) { return el.value; });
    }

    function matchVariant(opts) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < opts.length; j++) {
          if (String(v.options[j]) !== String(opts[j])) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    function update() {
      var v = matchVariant(selectedOptions());

      if (!v) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = STR.unavailable;
        }
        if (availability) availability.textContent = STR.unavailable;
        return;
      }

      if (idInput) idInput.value = v.id;
      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? STR.add_to_cart : STR.sold_out;
        addBtn.setAttribute("data-variant-id", v.id);
      }
      if (priceWrap && v.price_html) priceWrap.innerHTML = v.price_html;
      if (availability) {
        availability.innerHTML = v.available
          ? '<span class="in-stock">' + STR.in_stock + "</span>"
          : STR.sold_out;
      }
      if (mainImg && v.image) {
        /* Clear the srcset built for the previous image, otherwise the browser
           keeps serving a candidate from it and the swap looks like it failed. */
        mainImg.removeAttribute("srcset");
        mainImg.src = v.image;
      }

      /* Keep the URL shareable without reloading. */
      if (window.history.replaceState && v.id) {
        var u = new URL(window.location.href);
        u.searchParams.set("variant", v.id);
        window.history.replaceState({}, "", u.toString());
      }
    }

    qsa("[data-option-index]", root).forEach(function (el) {
      on(el, "change", update);
    });

    /* Quantity stepper */
    on(root, "click", function (e) {
      var input = qs("[data-qty-input]", root);
      if (!input) return;
      var dec = e.target.closest("[data-qty-dec]");
      var inc = e.target.closest("[data-qty-inc]");
      if (!dec && !inc) return;
      e.preventDefault();
      var val = parseInt(input.value, 10) || 1;
      val = dec ? Math.max(1, val - 1) : Math.min(99, val + 1);
      input.value = val;
    });

    /* Gallery thumbnails */
    on(root, "click", function (e) {
      var thumb = e.target.closest("[data-thumb]");
      if (!thumb || !mainImg) return;
      e.preventDefault();
      mainImg.removeAttribute("srcset");
      mainImg.src = thumb.getAttribute("data-thumb-src");
      mainImg.alt = thumb.getAttribute("data-thumb-alt") || mainImg.alt;
      qsa("[data-thumb]", root).forEach(function (t) { t.setAttribute("aria-current", "false"); });
      thumb.setAttribute("aria-current", "true");
    });

    /* Add to cart over AJAX so the drawer stays the single cart surface. */
    on(form, "submit", function (e) {
      e.preventDefault();
      var vid = idInput ? idInput.value : null;
      var qtyInput = qs("[data-qty-input]", root);
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      if (!vid) return;

      var label = addBtn ? addBtn.textContent : "";
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = STR.adding;
      }
      addToCart(vid, qty, { openDrawer: true })
        .catch(function () { /* message already surfaced */ })
        .then(function () {
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.textContent = label;
          }
        });
    });

    update();
  }

  qsa("[data-product-root]").forEach(initProductForm);

  /* ================================================================
     Collection sort
     ================================================================ */

  on(document, "change", function (e) {
    var sort = e.target.closest("[data-sort-by]");
    if (!sort) return;
    var u = new URL(window.location.href);
    u.searchParams.set("sort_by", sort.value);
    u.searchParams.delete("page");
    window.location.href = u.toString();
  });

  /* ================================================================
     Escape closes whatever is on top
     ================================================================ */

  on(document, "keydown", function (e) {
    if (e.key !== "Escape") return;
    if (cartDrawer && cartDrawer.classList.contains("open")) {
      closeDrawer();
      return;
    }
    var open = qsa(".product-block.info-open");
    if (open.length) {
      open.forEach(function (b) { setInfo(b, false); });
      return;
    }
    var openWaitlists = qsa(".product-meta.waitlist-open");
    if (openWaitlists.length) {
      openWaitlists.forEach(closeWaitlist);
      return;
    }
    qsa(".product-photo.is-zoomed").forEach(function (p) { p.classList.remove("is-zoomed"); });
  });

  /* ================================================================
     Theme editor support
     ================================================================ */

  document.addEventListener("shopify:section:load", function (e) {
    var root = e.target.querySelector("[data-section-root]") || e.target;
    initSearchFilter(root);
    qsa("[data-product-root]", e.target).forEach(initProductForm);
    syncHeaderHeight();
    if (window.NOOR_HEADER_SYNC) window.NOOR_HEADER_SYNC();
  });

  /* ================================================================
     Boot
     ================================================================ */

  (function boot() {
    var drawerRoot = document.getElementById("shopify-section-cart-drawer");
    if (drawerRoot) syncCartChrome(readCartCount(drawerRoot));
  })();
})();
